import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Download, TrendingDown, SwapHoriz } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { nonMovingItemsAPI, lookupAPI } from '../services/api';
import { Store, NonMovingItem, NonMovingItemsMetadata, OpenOrderDetail } from '../types';

interface Props {
  selectedStore: Store | null;
}

interface FlatRow {
  item: NonMovingItem;
  order: OpenOrderDetail | null;
}

function flattenRows(items: NonMovingItem[]): FlatRow[] {
  const flat: FlatRow[] = [];
  items.forEach(item => {
    if (item.open_orders && item.open_orders.length > 0) {
      item.open_orders.forEach(order => flat.push({ item, order }));
    } else {
      flat.push({ item, order: null });
    }
  });
  return flat;
}

const inr = (n: string | number | null) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function fmtDate(val: string | null) {
  if (!val) return '—';
  return dayjs(val).format('DD-MMM-YY');
}

function orderCellSx(order: OpenOrderDetail | null) {
  if (!order) return { color: 'text.disabled', fontWeight: 400 };
  return order.is_exact
    ? { color: '#c62828', fontWeight: 600 }
    : { color: '#e65100', fontWeight: 600, fontStyle: 'italic' as const };
}

const NonMovingItems: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const [storeId, setStoreId] = useState<number | ''>(selectedStore ? selectedStore.id : '');
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(3, 'month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [settleDays, setSettleDays] = useState('30');
  const [includeZeroStock, setIncludeZeroStock] = useState(false);
  const [rows, setRows] = useState<NonMovingItem[]>([]);
  const [metadata, setMetadata] = useState<NonMovingItemsMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Plain function, not memoized: it is recreated each render so it always reads the
  // current knob values, while the effect below still only refires on store/date change.
  // Editing a knob therefore needs an explicit Search rather than firing per keystroke.
  const loadReport = async () => {
    if (!storeId || !fromDate || !toDate) return;
    setLoading(true);
    setError('');
    try {
      const res = await nonMovingItemsAPI.getReport({
        store_id: storeId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        min_days_since_purchase: Number(settleDays),
        include_zero_stock: includeZeroStock,
      });
      setRows(res.data);
      setMetadata(res.metadata);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to load non-moving items report');
      setRows([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, fromDate, toDate, includeZeroStock]);

  const exportCSV = () => {
    const headers = [
      'Medicine', 'Manufacturer', 'Pack Size', 'Units Per Pack', 'Packs Purchased',
      'Qty Purchased (loose)', 'Qty Sold (loose)', 'Extra Ordered', 'Purchase Amount', 'In Stock',
      'Last Purchase', 'Days Since Purchase', 'Last Sale',
      'Recommended Transfer',
      'Order #', 'Order Date', 'Customer Name', 'Staff Name', 'Match Type',
    ];
    const csvRows = flattenRows(rows).map(({ item, order }) => [
      item.medicine_name,
      item.manufacturer_name,
      item.pack_size || '',
      item.units_per_pack,
      item.qty_period,
      item.qty_purchased_loose,
      item.qty_sold_period,
      item.excess_qty,
      item.amt_period,
      item.current_stock,
      item.last_purchase_date_in_period,
      item.days_since_purchase,
      item.last_sale_date || 'Never',
      item.transfer_recommendation
        ? `${item.transfer_recommendation.transfer_qty} to ${item.transfer_recommendation.to_store_name} (${item.transfer_recommendation.reason})`
        : '',
      order?.order_id ?? '',
      order?.order_date || '',
      order?.customer_name || '',
      order?.staff_name || '',
      order ? (order.is_exact ? 'Exact' : 'Approx (different strength/variant)') : '',
    ]);
    const csv = [headers, ...csvRows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `non_moving_items_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Store</InputLabel>
          <Select
            value={storeId}
            label="Store"
            onChange={e => setStoreId(e.target.value as number)}
          >
            {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>)}
          </Select>
        </FormControl>

        <DatePicker label="From" value={fromDate} onChange={setFromDate}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <DatePicker label="To" value={toDate} onChange={setToDate}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />

        <Tooltip title="An item is only called non-moving once this many days have passed since it was last purchased. Stops stock bought yesterday against a customer order from being flagged before it has had any chance to sell.">
          <TextField label="Settled for (days)" value={settleDays} onChange={e => setSettleDays(e.target.value)}
            size="small" type="number" sx={{ width: 150 }} />
        </Tooltip>

        <Tooltip title="By default only items you still hold are listed. Tick to also show items whose stock has already left the store.">
          <FormControlLabel
            control={<Checkbox size="small" checked={includeZeroStock} onChange={e => setIncludeZeroStock(e.target.checked)} />}
            label={<Typography variant="body2">Include zero stock</Typography>}
          />
        </Tooltip>

        <Button variant="outlined" onClick={loadReport} disabled={!storeId || loading}>
          Search
        </Button>

        <Button
          size="small"
          startIcon={<Download />}
          onClick={exportCSV}
          disabled={rows.length === 0}
          sx={{ ml: 'auto' }}
        >
          CSV
        </Button>
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Items purchased in this period with no sale in the 60 days before {toDate ? toDate.format('DD-MMM-YY') : 'the end date'}
        {' '}— that 60-day recency check is fixed and doesn't shrink even if you narrow the purchase date range above.
        Purchase amount and quantities reflect only the selected period.
        <strong> Qty Purchased and Qty Sold are both in loose units</strong> (tablets, not strips) so they
        are directly comparable — <strong>Extra Ordered</strong> is the difference, i.e. how much more was
        bought than moved. Hover Qty Purchased to see the pack maths.
        Only items you <strong>still hold</strong> are listed, and only once they have been sitting for at least{' '}
        <strong>{settleDays || 0} days</strong> since the last purchase — so stock bought yesterday against a customer
        order isn't flagged before it has had any chance to sell.
        A green <strong>Recommendation</strong> chip means this same item is fast-moving with low stock at another
        store right now — consider a transfer via Stock Transfers instead of buying more.
        Order columns in <Box component="span" sx={{ color: '#c62828', fontWeight: 600 }}>red</Box> are an exact item match;
        {' '}<Box component="span" sx={{ color: '#e65100', fontWeight: 600, fontStyle: 'italic' }}>amber italic</Box> means
        a different strength/variant of the same drug was ordered around the same time — a possible substitution, not a certain match.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && rows.length === 0 && (
        <Alert severity="info">
          No non-moving items found for this store and period — every item purchased was also sold since the period started.
        </Alert>
      )}

      {!loading && !error && rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Medicine</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Pack Size</TableCell>
                <TableCell align="right">Qty Purchased</TableCell>
                <TableCell align="right">Qty Sold</TableCell>
                <TableCell align="right">Extra Ordered</TableCell>
                <TableCell align="right">Purchase Amount</TableCell>
                <TableCell align="right">In Stock</TableCell>
                <TableCell>Last Purchase</TableCell>
                <TableCell>Last Sale</TableCell>
                <TableCell>Recommendation</TableCell>
                <TableCell sx={{ color: '#c62828', fontWeight: 600 }}>Order #</TableCell>
                <TableCell sx={{ color: '#c62828', fontWeight: 600 }}>Order Date</TableCell>
                <TableCell sx={{ color: '#c62828', fontWeight: 600 }}>Customer Name</TableCell>
                <TableCell sx={{ color: '#c62828', fontWeight: 600 }}>Staff Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flattenRows(rows).map(({ item, order }, idx) => (
                <TableRow key={`${item.manufacturer_id}-${order?.order_id ?? 'none'}-${idx}`} hover>
                  <TableCell>{item.medicine_name}</TableCell>
                  <TableCell>{item.manufacturer_name}</TableCell>
                  <TableCell>{item.pack_size || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${item.qty_period} pack${Number(item.qty_period) === 1 ? '' : 's'} × ${item.units_per_pack} = ${Number(item.qty_purchased_loose).toLocaleString('en-IN')} loose units`}>
                      <span>{Number(item.qty_purchased_loose).toLocaleString('en-IN')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{Number(item.qty_sold_period).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right"
                    sx={{ fontWeight: Number(item.excess_qty) > 0 ? 700 : 400,
                          color: Number(item.excess_qty) > 0 ? '#c62828' : 'text.disabled' }}>
                    <Tooltip title="Bought minus sold over the selected period — how much more was ordered than moved">
                      <span>{Number(item.excess_qty).toLocaleString('en-IN')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{inr(item.amt_period)}</TableCell>
                  <TableCell align="right"
                    sx={{ fontWeight: Number(item.current_stock) > 0 ? 700 : 400,
                          color: Number(item.current_stock) > 0 ? '#c62828' : 'text.disabled' }}>
                    {Number(item.current_stock).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell>
                    {fmtDate(item.last_purchase_date_in_period)}
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {item.days_since_purchase}d ago
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.last_sale_date
                      ? fmtDate(item.last_sale_date)
                      : <Chip size="small" label="Never" color="error" variant="outlined" />}
                  </TableCell>
                  <TableCell>
                    {item.transfer_recommendation ? (
                      <Tooltip title={item.transfer_recommendation.reason}>
                        <Chip
                          size="small"
                          color="success"
                          icon={<SwapHoriz />}
                          label={`${item.transfer_recommendation.transfer_qty} → ${item.transfer_recommendation.to_store_name}`}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={orderCellSx(order)}>
                    {order ? `${order.is_exact ? '' : '~'}${order.order_id}` : '—'}
                  </TableCell>
                  <TableCell sx={orderCellSx(order)}>
                    {order ? fmtDate(order.order_date) : '—'}
                  </TableCell>
                  <TableCell sx={orderCellSx(order)}>
                    {order ? (order.customer_name || '—') : '—'}
                  </TableCell>
                  <TableCell sx={orderCellSx(order)}>
                    {order ? (order.staff_name || '—') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {metadata?.capped && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', p: 1 }}>
              Showing first 2000 results — narrow the date range to see all items.
            </Typography>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default NonMovingItems;
